import React, { useRef, useEffect, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { PlayFill, PauseFill } from "@gravity-ui/icons";
import { usePostHog } from "posthog-js/react";
import * as Sentry from "@sentry/react"

const AdvancedEditAudioPlayer = ({ audioSrc, trackName, highlightRegions = null, onReady }) => {
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [duration, setDuration] = useState(0);

  const posthog = usePostHog();

  useEffect(() => {
    // Reset states when audioSrc changes
    setIsLoading(true);
    setHasError(false);
    setDuration(0); // Reset duration
    
    if (onReady && typeof onReady === 'function') {
      onReady(false);
    }
    
    if (!waveformRef.current) return;
    
    if (!audioSrc) {
      console.error("[AudioPlayer] No audio source provided");
      setIsLoading(false);
      setHasError(true);
      return;
    }

    if (wavesurfer.current) {
      wavesurfer.current.destroy();
    }

    const newWavesurferInstance = WaveSurfer.create({
      container: waveformRef.current,
      style: { zIndex: '10' },
      waveColor: 'rgba(255, 255, 255, 0.7)',
      progressColor: '#BA18EC',
      barWidth: 1,
      barGap: 1,
      barRadius: 4,
      height: 40,
      cursorWidth: 0,
      normalize: true,
      responsive: true
    });
    wavesurfer.current = newWavesurferInstance;

    let loadAttemptTimeoutId = null;
    let isLoadResolved = false;

    const resolveLoadAttempt = () => {
      isLoadResolved = true;
      if (loadAttemptTimeoutId) {
        clearTimeout(loadAttemptTimeoutId);
        loadAttemptTimeoutId = null;
      }
    };

    newWavesurferInstance.on('play', () => {
      setIsPlaying(true);
    });
    
    newWavesurferInstance.on('pause', () => {
      setIsPlaying(false);
    });
    
    newWavesurferInstance.on('finish', () => {
      setIsPlaying(false);
    });
    
    newWavesurferInstance.on('timeupdate', (time) => {
      setCurrentTime(time);
    });
    
    newWavesurferInstance.on('ready', () => {
      resolveLoadAttempt();
      if (waveformRef.current && wavesurfer.current === newWavesurferInstance) { // Check instance and mount
        setIsLoading(false);
        setHasError(false);
        const audioDuration = newWavesurferInstance.getDuration();
        setDuration(audioDuration);
        if (highlightRegions && highlightRegions.length > 0) {
          addHighlightRegions(newWavesurferInstance, highlightRegions, audioDuration);
        }
        if (onReady && typeof onReady === 'function') {
          onReady(true);
        }
      }
    });
    
    newWavesurferInstance.on('error', (err) => {
      resolveLoadAttempt();
      if (waveformRef.current && wavesurfer.current === newWavesurferInstance) { // Check instance and mount
        console.error("[AudioPlayer] WaveSurfer error:", err);
        setIsLoading(false);
        setHasError(true);
        if (onReady && typeof onReady === 'function') {
          onReady(false);
        }
      }
    });

    try {
      newWavesurferInstance.load(audioSrc);
      loadAttemptTimeoutId = setTimeout(() => {
        if (!isLoadResolved && wavesurfer.current === newWavesurferInstance && waveformRef.current) {
          console.warn(`[AudioPlayer] Load timeout for ${audioSrc}. Forcing error state.`);
          setIsLoading(false);
          setHasError(true);
          if (onReady && typeof onReady === 'function') {
            onReady(false);
          }
        }
      }, 15000); // 15 seconds timeout
    } catch (error) {
      Sentry.captureException(error);
      resolveLoadAttempt(); // Clear timeout if immediate error
      if (waveformRef.current && wavesurfer.current === newWavesurferInstance) { // Check instance and mount
        console.error("[AudioPlayer] Error loading audio:", error);
        setIsLoading(false);
        setHasError(true);
        if (onReady && typeof onReady === 'function') {
          onReady(false);
        }
      }
    }

    return () => {
      resolveLoadAttempt(); // Clear timeout on unmount/re-run
      if (newWavesurferInstance) {
        newWavesurferInstance.destroy();
      }
      if (wavesurfer.current === newWavesurferInstance) {
        wavesurfer.current = null;
      }
    };
  }, [audioSrc, highlightRegions, onReady]);

  // Function to add highlight regions to the waveform
  const addHighlightRegions = (wavesurferInstance, regions, audioDuration) => {
    try {
      // For WaveSurfer v7, we need to add regions directly to the waveform
      regions.forEach((region, index) => {
        try {
          // Convert normalized positions (0-1) to seconds
          const startTime = region.start * audioDuration;
          const endTime = region.end * audioDuration;
          
          console.log(`[AudioPlayer] Adding region ${index}:`, {
            start: startTime, 
            end: endTime, 
            color: region.color,
            label: region.label
          });
          
          // Add custom rendering for regions
          const regionElement = document.createElement('div');
          regionElement.style.position = 'absolute';
          regionElement.style.margin = `${3 * index}px 0px`;
          regionElement.style.height = `calc(100% - ${6 * index}px)`;
          regionElement.style.borderRadius = '8px';
          regionElement.style.borderWidth = '2px';
          regionElement.style.borderColor = region.color;
          regionElement.style.backgroundColor = region.color.replace(/,[^,]+\)$/, ',0.05)');
          regionElement.style.zIndex = 4; // Lower zIndex than waveform bars
          regionElement.style.top = 0;
          regionElement.style.pointerEvents = 'none';
          regionElement.setAttribute('data-label', region.label);
          
          // Calculate position and width
          const startPercent = (startTime / audioDuration) * 100;
          const widthPercent = ((endTime - startTime) / audioDuration) * 100;
          regionElement.style.left = `${startPercent}%`;
          regionElement.style.width = `${widthPercent}%`;
          
          // Add to waveform container - access container directly
          const container = waveformRef.current;
          if (container) { // Ensure container exists before appending
            container.appendChild(regionElement);
          }
        } catch (error) {
          console.error(`[AudioPlayer] Error adding region ${index}:`, error);
          Sentry.captureException(error);
        }
      });
    } catch (error) {
      console.error("[AudioPlayer] Error adding regions:", error);
      Sentry.captureException(error);
    }
  };

  // Update regions if highlightRegions changes
  useEffect(() => {
    if (wavesurfer.current && !isLoading && highlightRegions && highlightRegions.length > 0 && duration > 0) {
      console.log("[AudioPlayer] Updating highlight regions after load");
      
      // Clear any existing regions
      const container = waveformRef.current;
      if (container) {
        const existingRegions = container.querySelectorAll('div[data-label]');
        existingRegions.forEach(region => region.remove());
      }
      
      // Add new regions
      addHighlightRegions(wavesurfer.current, highlightRegions, duration);
    }
  }, [highlightRegions, isLoading, duration]);

  const handlePlayPause = () => {
    if (posthog) {
      posthog.capture("Click-PlayEditedTrack-AdvanceEditModal", {
        is_user_logged_in: true
      });
    } else {
      console.warn("[AudioPlayer] PostHog not available for event tracking");
    }
    if (wavesurfer.current && !isLoading && !hasError) {
      wavesurfer.current.playPause();
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="flex items-center bg-white/5 p-3 rounded-xl gap-3 w-full">
      <button
        onClick={handlePlayPause}
        className={`flex-shrink-0 flex items-center justify-center w-10 h-10 ${
          isLoading || hasError ? 'bg-white/5 text-white/10 cursor-not-allowed' : 'bg-white/10 hover:bg-white/20 text-white cursor-pointer'
        } rounded-full transition-colors focus:outline-none`}
        disabled={isLoading || hasError}
      >
        {isPlaying ? <PauseFill className="w-4 h-4" /> : <PlayFill className="w-4 h-4" />}
      </button>
      {/* Container for Track Info and Legend */}
      <div className="flex flex-col justify-center max-w-32 w-full">
        {/* Track Name and Time */}
        <div className="flex flex-col">
          <span className="text-white text-sm truncate">{trackName}</span>
          <span className="text-xs text-[#878188]">
            {hasError ? "Error loading audio" : (isLoading ? "Loading..." : formatTime(currentTime))}
          </span>
        </div>
        {/* Legend for highlight regions - Moved here */}
        {highlightRegions && highlightRegions.length > 0 && !isLoading && !hasError && (
          <div className="flex flex-wrap gap-x-2 gap-y-0 mt-1">
            {Array.from(new Set(highlightRegions.map(r => r.label))).map((label, idx) => {
              const region = highlightRegions.find(r => r.label === label);
              return (
                <div key={idx} className="flex items-center gap-1">
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: region.color.replace(/,[^,]+\)$/, ',1)') }} 
                  />
                  <span className="text-[10px] text-white">{label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
        {/* Waveform Container */}
      <div 
        ref={waveformRef} 
        onClick={() => {
          if (posthog) {
            posthog.capture("Click-OriginalTrack-AdvanceEditModal",{
              is_user_logged_in: true
            });
          }
        }} 
        className="flex-grow w-full py-1 items-center rounded-lg bg-white/5 h-12 min-w-0 relative"
      ></div> 
    </div>
  );
};

export default AdvancedEditAudioPlayer; 